/**
 * PermissionsContext — Fine-grained feature permissions for the current user.
 *
 * Reads the user's user_role_id from the profile (AuthContext), fetches their
 * permissions from Supabase, and exposes hasPermission() for use throughout
 * the app. Distinct from AuthContext which handles session/identity.
 *
 * Two-tier permission model:
 * - base_role (from AuthContext): controls which route trees are accessible
 * - user_role + permissions (this context): controls feature access within routes
 *
 * Defaults to open access (hasPermission → true) when no user_role_id is set,
 * preserving backward compatibility with roles that haven't been configured yet.
 */
import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PermissionsMap {
  [featureTitle: string]: { can_read: boolean; can_write: boolean };
}

/**
 * The shape of one row from the permissions query below
 * (`can_read, can_write, features(title)`). PostgREST returns a to-one embed
 * (`features`) as a single object in practice, but the generated relationship
 * types model it as an object-or-array, so we accept both shapes defensively.
 */
type PermissionRow = {
  can_read: boolean | null;
  can_write: boolean | null;
  features: { title: string } | { title: string }[] | null;
};

interface PermissionsContextValue {
  permissionsMap: PermissionsMap;
  hasPermission: (featureTitle: string, action: 'read' | 'write') => boolean;
  loading: boolean;
  refreshPermissions: () => void;
}

// ---------------------------------------------------------------------------
// Context (default: open access, no-op refresh)
// ---------------------------------------------------------------------------

const PermissionsContext = createContext<PermissionsContextValue>({
  permissionsMap: {},
  hasPermission: () => true,
  loading: false,
  refreshPermissions: () => undefined,
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [permissionsMap, setPermissionsMap] = useState<PermissionsMap>({});
  const [loading, setLoading] = useState(false);

  const userRoleId = profile?.user_role_id as string | null | undefined;

  const fetchPermissions = useCallback(async (roleId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('can_read, can_write, features(title)')
        .eq('user_role_id', roleId)
        .returns<PermissionRow[]>();

      if (error) throw error;

      const map: PermissionsMap = {};
      for (const p of data ?? []) {
        const feature = Array.isArray(p.features) ? p.features[0] : p.features;
        const title = feature?.title;
        if (title) {
          map[title] = { can_read: p.can_read ?? false, can_write: p.can_write ?? false };
        }
      }
      setPermissionsMap(map);
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setPermissionsMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userRoleId) {
      fetchPermissions(userRoleId);
    } else {
      setPermissionsMap({});
    }
  }, [userRoleId, fetchPermissions]);

  const refreshPermissions = useCallback(() => {
    if (userRoleId) fetchPermissions(userRoleId);
  }, [userRoleId, fetchPermissions]);

  const hasPermission = useCallback(
    (featureTitle: string, action: 'read' | 'write') => {
      const perm = permissionsMap[featureTitle];
      // No permission row → default open (role not yet configured)
      if (!perm) return true;
      return action === 'read' ? perm.can_read : perm.can_write;
    },
    [permissionsMap],
  );

  return (
    <PermissionsContext.Provider value={{ permissionsMap, hasPermission, loading, refreshPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePermissions() {
  return useContext(PermissionsContext);
}
